
import type { AutomaticVideoData } from '../types/automatic-video';
import type { Check } from '../types/check';
import type { FeedbackStars } from '../types/feedback';
import type { GameData } from '../types/game-data';
import type { Question } from '../types/question';
import type { ResultsRecord } from '../types/results';
import type { SignUpData } from '../types/sign-up';
import type { TeamResponse } from '../types/team';
import axios from 'axios';

const PLAYER_API = '/api/v1/player';

export type SubmitAnswerResponse = {
  text?: string;
  question?: Question;
  finalize?: boolean;
};

export class ApiService {
  transport;

  constructor(baseURL: string) {
    this.transport = axios.create({
      baseURL,
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    });
  }

  connect = (token: string, name: string, user_token?: string) =>
    this.transport.post<GameData>(`${PLAYER_API}/sessions`, {
      token,
      name,
      user_token,
    });

  check = (token: string) =>
    this.transport.get<Check>(`${PLAYER_API}/access`, {
      params: { token },
    });

  getQuestion = (token: string) =>
    this.transport.get<Question>(`${PLAYER_API}/questions/current`, {
      params: { token },
    });

  results = (token: string) =>
    this.transport.get<ResultsRecord[]>(`${PLAYER_API}/results`, {
      params: { token },
    });

  makeCaptain = (token: string, user_token: string) =>
    this.transport.patch(`${PLAYER_API}/team/captain`, { token, user_token });

  submitAnswer = (token: string, type: number, value: string, id?: string, stake?: number) =>
    this.transport.post<SubmitAnswerResponse>(`${PLAYER_API}/answers`, {
      token,
      type,
      value,
      id,
      stake,
    });

  submitButtons = (token: string, type: number, value: string, id?: string) =>
    this.transport.post<SubmitAnswerResponse>(`${PLAYER_API}/answers/buttons`, {
      token,
      type,
      value,
      id,
    });

  submitInteractive = (token: string, type: number, value: string, id?: string) =>
    this.transport.post<SubmitAnswerResponse>(`${PLAYER_API}/answers/interactive`, {
      token,
      type,
      value,
      id,
    });

  submitFeedback = (token: string, user_token: string, stars: FeedbackStars, text: string) =>
    this.transport.post<SubmitAnswerResponse>(`${PLAYER_API}/feedback`, {
      token,
      user_token,
      stars,
      text,
    });

  team = (token: string) =>
    this.transport.get<TeamResponse>(`${PLAYER_API}/team`, {
      params: { token },
    });

  signUp = (data: SignUpData) =>
    this.transport.post<{ url: string }>(`${PLAYER_API}/registrations`, data);

  checkAutomaticVideo = (token: string) =>
    this.transport.get<AutomaticVideoData>(`${PLAYER_API}/automatic-video`, {
      params: { token },
    });
}
